import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Mail, X, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  getMyAccount, listTeamMembers, inviteTeammate, revokeInvite, resendInvite,
  removeTeamMember, updateMemberRole,
} from "@/lib/settings.functions";

export const Route = createFileRoute("/_authenticated/settings/team")({
  component: TeamSettings,
});

const PRO_PLANS = ["pro_affiliate", "super_affiliate", "comped"];

function TeamSettings() {
  const queryClient = useQueryClient();
  const fetchAccount = useServerFn(getMyAccount);
  const fetchMembers = useServerFn(listTeamMembers);
  const invite = useServerFn(inviteTeammate);
  const revoke = useServerFn(revokeInvite);
  const resend = useServerFn(resendInvite);
  const remove = useServerFn(removeTeamMember);
  const changeRole = useServerFn(updateMemberRole);

  const { data: acctData } = useQuery({ queryKey: ["my-account"], queryFn: () => fetchAccount() });
  const account = acctData?.account;
  const myRole = acctData?.myRole;

  const { data, isLoading } = useQuery({
    queryKey: ["team", account?.id],
    queryFn: () => fetchMembers({ data: { account_id: account!.id } }),
    enabled: !!account?.id,
  });

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "manager" | "viewer">("manager");
  const [sending, setSending] = useState(false);

  if (!account) {
    return <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  const planAllows = account.is_admin_comped || PRO_PLANS.includes(account.plan);
  const canManage = myRole === "owner" || myRole === "admin";

  if (!planAllows) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-base font-medium">Team is a Pro feature</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Invite up to {account.team_member_limit} teammates with role-based access on the Pro Affiliate plan.
        </p>
        <Link to="/settings/billing"><Button className="mt-4">Upgrade plan</Button></Link>
      </div>
    );
  }

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["team", account.id] });

  const handleInvite = async () => {
    if (!email) return;
    setSending(true);
    try {
      await invite({ data: { account_id: account.id, email, role } });
      toast.success(`Invite sent to ${email}`);
      setEmail("");
      refresh();
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setSending(false); }
  };

  const handleRevoke = async (id: string) => {
    try { await revoke({ data: { invite_id: id } }); refresh(); } catch (e: any) { toast.error(e?.message); }
  };
  const handleResend = async (id: string) => {
    try { await resend({ data: { invite_id: id } }); toast.success("Resent"); } catch (e: any) { toast.error(e?.message); }
  };
  const handleRemove = async (user_id: string) => {
    try { await remove({ data: { account_id: account.id, user_id } }); refresh(); } catch (e: any) { toast.error(e?.message); }
  };
  const handleRoleChange = async (user_id: string, newRole: any) => {
    try {
      await changeRole({ data: { account_id: account.id, user_id, role: newRole } });
      refresh();
    } catch (e: any) { toast.error(e?.message); }
  };

  const members = data?.members ?? [];
  const invites = data?.invites ?? [];

  return (
    <div className="space-y-6">
      {canManage && (
        <section className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-base font-medium">Invite a teammate</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {members.length} of {account.team_member_limit} seats used.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_180px_auto]">
            <div>
              <Label htmlFor="inv-email">Email</Label>
              <Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@example.com" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={(v) => setRole(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={handleInvite} disabled={sending || !email}>
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                Send invite
              </Button>
            </div>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-base font-medium">Members</h2>
        {isLoading ? (
          <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="mt-4 divide-y divide-border">
            {members.map((m: any) => (
              <div key={m.id} className="flex items-center justify-between gap-4 py-3">
                <div>
                  <p className="text-sm font-medium">{m.profiles?.full_name ?? m.profiles?.email}</p>
                  <p className="text-xs text-muted-foreground">{m.profiles?.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  {canManage && m.role !== "owner" ? (
                    <Select value={m.role} onValueChange={(v) => handleRoleChange(m.user_id, v)}>
                      <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="rounded bg-muted px-2 py-1 text-xs">{m.role}</span>
                  )}
                  {canManage && m.role !== "owner" && (
                    <Button size="sm" variant="ghost" onClick={() => handleRemove(m.user_id)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {invites.length > 0 && (
          <>
            <h3 className="mt-6 text-sm font-medium">Pending invites</h3>
            <div className="mt-2 divide-y divide-border">
              {invites.map((inv: any) => (
                <div key={inv.id} className="flex items-center justify-between gap-4 py-3">
                  <div>
                    <p className="text-sm">{inv.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleResend(inv.id)}>
                        <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Resend
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRevoke(inv.id)}>
                        Revoke
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
